import { useApolloClient, useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { ContentArticleStatus } from '@xing-com/crate-common-graphql-types';

import { EntityPagesArticleModuleArticlesArticleDisableDocument } from '../graphql/mutations/entity-pages-article-module-articles-article-disable.gql-types';
import { EntityPagesArticleModuleContentArticleDisableDocument } from '../graphql/mutations/entity-pages-article-module-content-article-disable.gql-types';
import { EntityPagesArticleModuleListDocument } from '../graphql/queries/entity-pages-article-module-list.gql-types';
import type { ArticleCardView } from '../types/types';

import { useArticlesModuleContext } from './use-articles-module-context/use-articles-module-context';

export const useDisableArticleMutation = (): [
  (article: ArticleCardView) => Promise<void>,
  { loading: boolean },
] => {
  const { variablesQuery, refetch } = useArticlesModuleContext();
  const apolloClient = useApolloClient();
  const { $t } = useIntl();

  const [handleContentArticleDisable, contentArticleState] = useMutation(
    EntityPagesArticleModuleContentArticleDisableDocument
  );
  const [handleArticlesArticleDisable, articlesArticleState] = useMutation(
    EntityPagesArticleModuleArticlesArticleDisableDocument
  );

  return [
    useCallback(
      async (article: ArticleCardView) => {
        try {
          if (article.typename === 'ContentArticle') {
            const { data, errors } = await handleContentArticleDisable({
              variables: {
                input: { globalId: article.globalId },
              },
            });

            const error = data?.contentDisableArticle?.error || errors?.[0];

            if (error) {
              throw Error($t({ id: 'EP_GENERAL_FEEDBACK_ERROR' }), {
                cause: error,
              });
            }
          } else {
            const { data, errors } = await handleArticlesArticleDisable({
              variables: {
                input: { articleGlobalId: article.globalId },
              },
            });

            const error =
              data?.articlesDisableArticle?.__typename === 'ArticlesError' ||
              errors?.[0];

            if (error) {
              throw Error($t({ id: 'EP_GENERAL_FEEDBACK_ERROR' }), {
                cause: error,
              });
            }
          }

          const existingData = apolloClient.readQuery({
            query: EntityPagesArticleModuleListDocument,
            variables: variablesQuery,
          });

          if (
            existingData?.contentPageFeaturedArticle?.object.id === article.id
          ) {
            refetch();
            return;
          }

          const { viewer, contentPageArticles } = existingData ?? {};
          const existingMetadata = existingData?.viewer?.contentPage?.metadata;
          if (
            viewer === undefined ||
            viewer?.contentPage === undefined ||
            existingMetadata?.publishedArticlesCount === undefined ||
            existingMetadata?.disabledArticlesCount === undefined ||
            contentPageArticles === undefined ||
            contentPageArticles?.edges === undefined
          ) {
            refetch();
            return;
          }

          const newViewer = {
            ...viewer,
            contentPage: {
              ...viewer.contentPage,
              metadata: {
                ...existingMetadata,
                publishedArticlesCount:
                  existingMetadata.publishedArticlesCount - 1,
                disabledArticlesCount:
                  existingMetadata.disabledArticlesCount + 1,
              },
            },
          };

          let newContentPageArticles;
          switch (variablesQuery.status) {
            case undefined: {
              newContentPageArticles = {
                ...contentPageArticles,
                edges: contentPageArticles.edges.map((edge) =>
                  edge?.node?.object?.id !== article.id
                    ? edge
                    : {
                        ...edge,
                        node: {
                          ...edge?.node,
                          status: ContentArticleStatus.Disabled,
                        },
                      }
                ),
              };
              break;
            }
            default: {
              newContentPageArticles = {
                ...contentPageArticles,
                edges: contentPageArticles.edges.filter(
                  (edge) => edge?.node.object.id !== article.id
                ),
              };
              break;
            }
          }
          apolloClient.cache.writeQuery({
            query: EntityPagesArticleModuleListDocument,
            variables: variablesQuery,
            data: {
              ...existingData,
              viewer: newViewer,
              contentPageArticles: newContentPageArticles,
            },
          });
        } catch (error) {
          throw Error($t({ id: 'EP_GENERAL_FEEDBACK_ERROR' }), {
            cause: error,
          });
        }
      },
      [
        handleArticlesArticleDisable,
        handleContentArticleDisable,
        $t,
        apolloClient,
        variablesQuery,
        refetch,
      ]
    ),
    { loading: contentArticleState.loading || articlesArticleState.loading },
  ];
};
